import React, { useState } from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';

const VisionAssessmentTool = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const assessmentQuestions = [
    {
      id: 'vision_clarity',
      question: "How would you rate your current vision clarity?",
      type: 'radio',
      options: [
        { value: 'excellent', label: 'Excellent - I can see clearly at all distances', score: 0 },
        { value: 'good', label: 'Good - Minor issues with reading or distance vision', score: 1 },
        { value: 'fair', label: 'Fair - Noticeable difficulty with daily activities', score: 2 },
        { value: 'poor', label: 'Poor - Significant vision problems affecting my life', score: 3 }
      ]
    },
    {
      id: 'eye_strain',
      question: "Do you experience eye strain or fatigue?",
      type: 'radio',
      options: [
        { value: 'never', label: 'Never or rarely', score: 0 },
        { value: 'sometimes', label: 'Sometimes, especially after screen time', score: 1 },
        { value: 'often', label: 'Often, even with normal activities', score: 2 },
        { value: 'always', label: 'Constantly throughout the day', score: 3 }
      ]
    },
    {
      id: 'symptoms',
      question: "Which symptoms have you experienced recently?",
      type: 'checkbox',
      options: [
        { value: 'headaches', label: 'Frequent headaches', score: 1 },
        { value: 'dry_eyes', label: 'Dry or irritated eyes', score: 1 },
        { value: 'blurry_vision', label: 'Blurry or double vision', score: 2 },
        { value: 'light_sensitivity', label: 'Sensitivity to light', score: 1 },
        { value: 'night_vision', label: 'Difficulty seeing at night', score: 2 },
        { value: 'none', label: 'None of the above', score: 0 }
      ]
    },
    {
      id: 'age_group',
      question: "What is your age group?",
      type: 'radio',
      options: [
        { value: 'under_30', label: 'Under 30', score: 0 },
        { value: '30_45', label: '30-45 years', score: 1 },
        { value: '45_60', label: '45-60 years', score: 2 },
        { value: 'over_60', label: 'Over 60 years', score: 3 }
      ]
    },
    {
      id: 'family_history',
      question: "Do you have a family history of eye conditions?",
      type: 'radio',
      options: [
        { value: 'no', label: 'No known family history', score: 0 },
        { value: 'minor', label: 'Minor conditions (glasses, mild issues)', score: 1 },
        { value: 'major', label: 'Major conditions (glaucoma, cataracts, diabetes)', score: 2 },
        { value: 'unknown', label: 'I\'m not sure', score: 1 }
      ]
    }
  ];

  const handleAnswer = (questionId, value, score) => {
    if (assessmentQuestions[currentStep].type === 'checkbox') {
      const currentAnswers = answers[questionId] || [];
      const isSelected = currentAnswers.some(answer => answer.value === value);
      
      if (value === 'none') {
        setAnswers(prev => ({ ...prev, [questionId]: [{ value, score }] }));
      } else {
        if (isSelected) {
          setAnswers(prev => ({
            ...prev,
            [questionId]: currentAnswers.filter(answer => answer.value !== value && answer.value !== 'none')
          }));
        } else {
          setAnswers(prev => ({
            ...prev,
            [questionId]: [...currentAnswers.filter(answer => answer.value !== 'none'), { value, score }]
          }));
        }
      }
    } else {
      setAnswers(prev => ({ ...prev, [questionId]: [{ value, score }] }));
    }
  };

  const calculateScore = () => {
    let totalScore = 0;
    Object.values(answers).forEach(answerArray => {
      answerArray.forEach(answer => {
        totalScore += answer.score;
      });
    });
    return totalScore;
  };

  const getRecommendation = (score) => {
    if (score <= 3) {
      return {
        level: 'Low Risk',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: 'CheckCircle',
        title: 'Your Vision Health Looks Good!',
        description: 'Based on your responses, you appear to have good eye health. We recommend annual check-ups to maintain optimal vision.',
        action: 'Schedule Annual Check-up'
      };
    } else if (score <= 7) {
      return {
        level: 'Moderate Risk',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        icon: 'AlertTriangle',
        title: 'Some Concerns Detected',
        description: 'You may benefit from a comprehensive eye examination to address potential issues early.',
        action: 'Book Comprehensive Exam'
      };
    } else {
      return {
        level: 'High Priority',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        icon: 'AlertCircle',
        title: 'Immediate Attention Recommended',
        description: 'Your responses suggest you should see an eye care professional soon for proper evaluation and care.',
        action: 'Schedule Urgent Consultation'
      };
    }
  };

  const nextStep = () => {
    if (currentStep < assessmentQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetAssessment = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };

  const currentQuestion = assessmentQuestions[currentStep];
  const currentAnswers = answers[currentQuestion?.id] || [];
  const canProceed = currentAnswers.length > 0;

  if (showResults) {
    const score = calculateScore();
    const recommendation = getRecommendation(score);

    return (
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-4" style={{ color: "#08005f" }}>
              Your Vision Assessment Results
            </h2>
            <p className="text-lg text-text-secondary">
              Based on your responses, here's what we recommend for your eye health
            </p>
          </div>

          <div className={`${recommendation.bgColor} rounded-2xl p-8 medical-shadow`}>
            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-16 h-16 ${recommendation.bgColor} rounded-full mb-4`}>
                <Icon name={recommendation.icon} size={32} color={recommendation.color.replace('text-', '')} />
              </div>
              <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
                {recommendation.title}
              </h3>
              <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${recommendation.color} bg-white`}>
                Risk Level: {recommendation.level}
              </div>
            </div>

            <p className="text-center text-text-secondary mb-8 max-w-2xl mx-auto">
              {recommendation.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                 onClick={() => window.location.href = "/book-appointment-guided-care-journey"}
              >
                {recommendation.action}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={resetAssessment}
              >
                Take Assessment Again
              </Button>
            </div>

            <div className="mt-8 p-4 bg-white rounded-lg">
              <div className="flex items-center justify-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={16} />
                  <span>Confidential Assessment</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} />
                  <span>Results Valid for 30 Days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-heading font-bold text-text-primary mb-4" style={{ color: "#08005f" }}>
            Free Vision Health Assessment
          </h2>
          <p className="text-lg text-text-secondary">
            Take our quick 2-minute assessment to understand your eye health needs
          </p>
        </div>

        <div className="bg-white rounded-2xl medical-shadow overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-gray-100 h-2">
            <div 
              className="bg-primary h-full medical-transition"
              style={{ width: `${((currentStep + 1) / assessmentQuestions.length) * 100}%` }}
            />
          </div>

          <div className="p-8">
            {/* Question Counter */}
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-text-secondary">
                Question {currentStep + 1} of {assessmentQuestions.length}
              </div>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Clock" size={16} />
                <span>~2 minutes remaining</span>
              </div>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-6">
                {currentQuestion.question}
              </h3>

              <div className="space-y-3">
                {currentQuestion.options.map((option) => {
                  const isSelected = currentAnswers.some(answer => answer.value === option.value);
                  
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(currentQuestion.id, option.value, option.score)}
                      className={`w-full text-left p-4 rounded-lg border-2 medical-transition ${
                        isSelected 
                          ? 'border-primary bg-red-50 text-primary' :'border-gray-200 hover:border-gray-300 text-text-primary'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-primary bg-primary' : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <Icon name="Check" size={12} color="white" />
                          )}
                        </div>
                        <span className="font-medium">{option.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 0}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>

              <Button
                variant="default"
                onClick={nextStep}
                disabled={!canProceed}
                iconName={currentStep === assessmentQuestions.length - 1 ? "Eye" : "ChevronRight"}
                iconPosition="right"
              >
                {currentStep === assessmentQuestions.length - 1 ? "Get Results" : "Next"}
              </Button>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-6 text-sm text-text-secondary">
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={16} />
              <span>Secure & Private</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="UserCheck" size={16} />
              <span>Developed by Eye Specialists</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={16} />
              <span>Medically Validated</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionAssessmentTool;